import { FC, useContext } from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router'

import { Descriptions, Flex, Popover, Tabs, Typography } from 'antd'

import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import tz from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { useTrackQuery } from '../../graphql'
import { client } from '../../services/motogp'

import { Context } from './Context'
import { ResultTable } from './ResultTable'

dayjs.extend(tz)
dayjs.extend(utc)

const ResultTabs: FC = () => {
  const { formatMessage } = useIntl()
  const {
    state: { eventId, categoryId, seasonId },
  } = useContext(Context)
  const { data } = useQuery({
    queryKey: ['sessions', seasonId, eventId, categoryId],
    queryFn: () => client.sessions(eventId, categoryId),
    enabled: !!categoryId && !!eventId,
  })

  const { track: trackSlug = '' } = useParams()
  const events = data?.slice()?.sort((a, b) => dayjs(b.date).diff(a.date))

  const trackQuery = useTrackQuery({ variables: { slug: trackSlug } })
  const defaultActiveKey =
    events?.find((it) => {
      const date = dayjs(it.date)
        .tz(trackQuery.data?.track?.timezone ?? undefined, true)
        .local()
      return dayjs().isAfter(date) ? it : null
    }) ?? data?.[0]

  return defaultActiveKey && events ? (
    <Tabs
      defaultActiveKey={defaultActiveKey.id}
      items={events?.map((it) => {
        const conditions = [
          { label: formatMessage({ id: 'conditions.air' }), children: it.condition.air },
          { label: formatMessage({ id: 'conditions.ground' }), children: it.condition.ground },
          { label: formatMessage({ id: 'conditions.humidity' }), children: it.condition.humidity },
          {
            label: formatMessage({ id: 'conditions.track' }),
            children: it.condition.track
              ? formatMessage({ id: `conditions.track.${it.condition.track.toLowerCase()}` })
              : '',
          },
          {
            label: formatMessage({ id: 'conditions.weather' }),
            children: it.condition.weather
              ? formatMessage({ id: `conditions.weather.${it.condition.weather.toLowerCase()}` })
              : '',
          },
        ].filter((it) => it.children.length > 1)
        return {
          label: (
            <Popover
              title={dayjs(it.date)
                .tz(trackQuery.data?.track?.timezone ?? undefined, true)
                .local()
                .format('MM/DD HH:mm')}
            >
              <Typography.Text style={{ textAlign: 'center' }}>
                {it.number ? [it.type, it.number].join('') : it.type}
              </Typography.Text>
            </Popover>
          ),
          key: it.id,
          children: (
            <Flex vertical gap={16}>
              {conditions.length > 0 ? (
                <Descriptions
                  size={'small'}
                  column={{ xs: 2, sm: 2, md: 5, lg: 5, xl: 5, xxl: 5 }}
                  layout={'horizontal'}
                  items={conditions}
                />
              ) : null}

              <ResultTable sessionId={it.id} />
            </Flex>
          ),
        }
      })}
    />
  ) : null
}

export { ResultTabs }
